import React, { useState, useEffect } from 'react';
import { Product } from '../types';
import { productsApi } from '../lib/api';
import { mapProduct } from '../lib/mappers';
import { ProductCard } from './ProductCard';
import { placeholders } from '../lib/placeholders';

interface ProductGridProps {
  title: string;
  filter?: 'featured' | 'bestseller';
  limit?: number;
}

export const ProductGrid: React.FC<ProductGridProps> = ({ title, filter, limit = 8 }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const isAlternateSurface = filter !== 'bestseller';

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const { data, error } = await productsApi.getAll({
          featured: filter === 'featured' ? true : undefined,
          bestseller: filter === 'bestseller' ? true : undefined,
          limit,
        });

        if (error) {
          console.error('❌ Failed to load products:', error);
          setLoading(false);
          return;
        }

        const productsList = data?.products || (Array.isArray(data) ? data : []);

        if (!productsList || productsList.length === 0) {
          console.warn('⚠️ No products found in response');
        }

        const mapped = Array.isArray(productsList) ? productsList.map(mapProduct) : [];

        // Additional frontend filter as fallback to ensure only bestseller products are shown
        const filtered = filter === 'bestseller'
          ? mapped.filter((product) => product.is_bestseller === true).slice(0, limit)
          : mapped.slice(0, limit);
        setProducts(filtered);
      } catch (err) {
        console.error('Error fetching products:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [filter, limit]);

  // For featured collection, show first 4 in arched display, rest in grid
  const featuredProducts = filter === 'featured' ? products.slice(0, 4) : [];
  const remainingProducts = filter === 'featured' ? products.slice(4) : products;

  return (
    <section className={`py-16 ${isAlternateSurface ? 'bg-secondary/75' : 'bg-background'}`}>
      <div className="container mx-auto px-4 lg:px-8">
        <div className="mb-12 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-primary/75">Curated selection</p>
          <h2 className="mt-3 text-4xl font-bold text-text">
            {title} <span className="text-primary">Collection</span>
          </h2>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-charcoal/70">Loading products...</p>
          </div>
        ) : products.length > 0 ? (
          <>
            {/* Arched Display for Featured Products */}
            {filter === 'featured' && featuredProducts.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                {featuredProducts.map((product, index) => {
                  const bgColors = [
                    'bg-background',
                    'bg-secondary/80',
                    'bg-white/70',
                    'bg-secondary/60',
                  ];

                  const image = product.image_url || product.images?.[0] || placeholders.product;
                  const comparePrice = product.compare_at_price ?? product.originalPrice ?? null;
                  const discountPercent = comparePrice
                    ? Math.round(((comparePrice - product.price) / comparePrice) * 100)
                    : 0;

                  return (
                    <a
                      key={product.id}
                      href={`/product/${product.slug || product.id}`}
                      className="group relative overflow-hidden rounded-[30px] border border-primary/10 transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_20px_46px_rgba(13,13,13,0.1)]"
                    >
                      <div className={`${bgColors[index % bgColors.length]} relative flex h-96 flex-col items-center justify-center p-8 md:h-[420px]`}>
                        {discountPercent > 0 && (
                          <div className="absolute left-5 top-5 z-20 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-white shadow-lg md:text-sm">
                            {discountPercent}% OFF
                          </div>
                        )}

                        <div className="relative z-10 mb-4">
                          <div className="h-40 w-40 overflow-hidden rounded-full border-4 border-white bg-white/90 shadow-2xl transition-transform duration-500 group-hover:scale-105 md:h-48 md:w-48">
                            <img
                              src={image}
                              alt={product.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </div>

                        <div className="relative z-10 text-center px-2">
                          {product.deity && (
                            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-primary/80">
                              {product.deity}
                            </p>
                          )}
                          <h3 className="mb-2 line-clamp-2 text-base font-bold text-text transition-colors group-hover:text-primary md:text-lg">
                            {product.name}
                          </h3>
                          <div className="flex items-center justify-center gap-2 mb-2">
                            <span className="text-lg font-bold text-text md:text-xl">₹{product.price}</span>
                            {comparePrice && (
                              <span className="text-sm text-charcoal/50 line-through">
                                ₹{comparePrice}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </a>
                  );
                })}
              </div>
            )}

            {/* Regular Grid for Remaining Products (only for featured) */}
            {filter === 'featured' && remainingProducts.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {remainingProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}

            {/* If not featured, show all in regular grid */}
            {filter !== 'featured' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-charcoal/70">No products found</p>
            <p className="mt-2 text-sm text-charcoal/50">Check browser console for details</p>
          </div>
        )}
      </div>
    </section>
  );
};
